<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Spinner } from '@sveltestrap/sveltestrap';

  import { CodeMirror } from '@client/components/codemirror';
  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { SignaturesService } from '@client/services/signatures';

  import { minifyHTML } from '@client/utils/html-formatter';
  import { resizeIframe } from '@client/utils/iframe';

  const { addNotification } = getNotificationsContext();

  export let email = undefined;
  export let type = undefined;

  const editTypes = {
    google: 'gapps',
    salesforce: 'sfdc',
    front: 'frontapp'
  };

  let editing = false;
  let loading = false;
  let originalTemplate = '';
  let updating = false;
  let signature;

  $: editType = editTypes[type] || type;

  const notify = (text, error = false) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onEditSignature = () => {
    originalTemplate = signature.slice();
    editing = true;
  };

  const onUpdateSignature = async () => {
    const html = minifyHTML(signature);

    updating = true;

    const response = await SignaturesService.updateDefaultSignatures(email, email, editType, html);

    updating = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE_PUSHED_SIGNATURE'), true);
    } else {
      notify($format('label.SUCCESS.UPDATE_PUSHED_SIGNATURE'));
      editing = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const response = await SignaturesService.getDefaultSignatures(email, email, editType);

      switch (type) {
        case 'outreach':
          signature = response?.data?.attributes?.emailSignature;
          break;
        default:
          signature = response?.result;
      }

      originalTemplate = signature.slice();
    } catch (e) {
      console.error(e);
    } finally {
      loading = false;
    }
  });
</script>

<div class="pushed-signature" {...$$restProps}>
  {#if loading}
    <div class="text-center py-5">
      <Spinner color="primary" />
    </div>
  {:else if signature}
    <div class="text-end pb-1">
      {#if !editing}
        <Button color="primary" class="mx-2 me-0" size="sm" on:click={onEditSignature}>
          {$format('label.EDIT_SIGNATURE')}
        </Button>
      {:else}
        <Button
          class="me-1"
          disabled={updating}
          size="sm"
          on:click={() => {
            editing = false;
            signature = originalTemplate;
          }}
        >
          {$format('label.CANCEL')}
        </Button>
        <Button color="primary" disabled={updating} size="sm" on:click={onUpdateSignature}>
          {$format(updating ? 'label.SAVING' : 'label.UPDATE')}
        </Button>
      {/if}
    </div>
    <iframe
      class="w-100"
      tabindex="-1"
      title={email}
      on:load={resizeIframe}
      srcdoc="<html><head></head><body><div style='display: inline-block; padding: 0; margin: 0;'>{signature}</div></body></html>"
    />

    {#if editing}
      <CodeMirror bind:value={signature} readonly={updating} lineWrapping formatOnLoad />
    {/if}
  {:else}
    <span class="text-danger">
      <Icon name="alert-triangle" />
      {$format('label.NO_SIGNATURE')}
    </span>
  {/if}
</div>

<style lang="scss">
  .pushed-signature {
    :global(.codemirror-wrapper) {
      max-height: 30rem;
      overflow: auto;
    }
  }
</style>
